import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import { isDefined } from 'ts-utils';

type VideoEmbedProps = {
  src: string;
  title?: string;
  maxWidth?: number;
};

/**
 * VideoEmbed Component
 *
 * Renders a responsive Vimeo video embed via iframe.
 * Uses 9:16 (portrait) aspect ratio.
 * Mobile-first design: full width on small screens, max-width constraint on desktop.
 *
 * Use Vimeo player URL (e.g., https://player.vimeo.com/video/1170908260?...)
 */
export const VideoEmbed: FunctionComponent<VideoEmbedProps> = ({
  src,
  title = 'Video',
  maxWidth = 800,
}) => {
  return (
    <VideoWrapper maxWidth={maxWidth}>
      <VideoContainer>
        <iframe
          src={src}
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          title={title}
        />
      </VideoContainer>
    </VideoWrapper>
  );
};

// Wrapper with max-width and centering
const VideoWrapper = styled.div<{ maxWidth?: number }>`
  width: 100%;
  max-width: ${({ maxWidth }) => (isDefined(maxWidth) ? `${maxWidth}px` : '800px')};
  margin: 0 auto 32px auto;
`;

// Container with 9:16 portrait aspect ratio using padding-bottom trick
const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 177.78%; /* 9:16 portrait aspect ratio */
  height: 0;
  overflow: hidden;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;
